import * as Tone from 'tone';
import { getMetronomePlaybackPatterns, MetronomeSettings } from './metronome';
import { getTotalDuration, getPlaybackPatternsForNoteGroup } from './note';
import { PlaybackPattern, Pitch } from './note-definition';
import { TimeSignature, TimeSignatureComplexity } from './time-signature';
import { Measure } from './vex';

export enum PlaybackState {
  PLAYING,
  STOPPED,
}
export type NoteTriggerHandler = (index: number | null) => void;

// iOS silences Web Audio while the ring/silent switch is engaged, unless the
// page says it is playing media rather than making incidental sounds. Safari
// 16.4 and up take that from the Audio Session API; every other browser simply
// has no `audioSession` and needs nothing, which is why the sound has only
// ever been missing on iPhones.
const claimPlaybackAudioSession = () => {
  const { audioSession } = navigator as Navigator & {
    audioSession?: { type: string };
  };

  if (audioSession) {
    audioSession.type = 'playback';
  }
};

// Starts the audio context. Must stay reachable synchronously from the user
// gesture that asked for sound, or iOS refuses to start it at all.
const startAudio = () => {
  claimPlaybackAudioSession();
  Tone.start();
};

const toneDurationFactorNoteSpacingMap: { [toneDuration: string]: number } = {
  '1': 0.9,
  '2': 0.875,
  '4': 0.75,
  '8': 0.67,
  '16': 0.5,
  '32': 0.1,
};

const NOTE_SPACING = 0.75; // %
const HEADING_TIME = 0.1; // seconds
const TRAILING_TIME = 1; // seconds

export const Transport = Tone.Transport;

// Any monophonic Tone instrument that exposes triggerAttackRelease. Both the
// bass playback voice and the metronome click use the same trigger helpers.
type TriggerableSynth = Tone.MonoSynth | Tone.MembraneSynth;

// Percussive bass "pluck": a low triangle voice with a fast filter envelope,
// so each note has a defined attack that quickly decays away instead of the
// thin, sustained tone of the default Tone.Synth.
const getPlaybackSynth = (): TriggerableSynth => {
  const synth = new Tone.MonoSynth({
    oscillator: { type: 'triangle' },
    envelope: {
      attack: 0.005,
      decay: 0.28,
      sustain: 0.25,
      release: 0.4,
    },
    filterEnvelope: {
      attack: 0.005,
      decay: 0.18,
      sustain: 0.05,
      release: 0.3,
      baseFrequency: 90,
      octaves: 3.5,
      exponent: 2,
    },
  }).toDestination();

  synth.filter.Q.value = 2;

  return synth;
};

// Dry metronome click: a short MembraneSynth "tick" with almost no pitch
// decay and a very short amplitude envelope, so it reads as a percussive
// click rather than a piercing sine beep.
const getMetronomeSynth = (): TriggerableSynth => {
  const synth = new Tone.MembraneSynth({
    pitchDecay: 0.008,
    octaves: 2,
    envelope: {
      attack: 0.001,
      decay: 0.05,
      sustain: 0,
      release: 0.02,
    },
  }).toDestination();

  return synth;
};

export const startPlayback = (
  measures: Measure[],
  pitch: Pitch,
  timeSignature: TimeSignature,
  metronomeSettings: MetronomeSettings,
  onNoteTrigger: NoteTriggerHandler,
  onMetronomeClickTrigger: NoteTriggerHandler,
  // Number of times to play the rhythm. Use Infinity to loop until stopped.
  repeat: number = 1
) => {
  startAudio();
  Transport.cancel();
  Transport.loop = false;

  Transport.timeSignature = timeSignature.beatsPerMeasure;

  const isInfinite = !isFinite(repeat);
  const passes = isInfinite ? 1 : Math.max(1, Math.floor(repeat));

  const playbackSynth = getPlaybackSynth();
  const metronomeSynth = metronomeSettings.active ? getMetronomeSynth() : null;

  let elapsedTime = HEADING_TIME;

  // Count-off clicks play once, before the repeated/looped body.
  if (metronomeSynth && metronomeSettings.countOffMeasures) {
    scheduleMetronomeMeasures(
      metronomeSettings.countOffMeasures,
      metronomeSynth,
      elapsedTime,
      timeSignature,
      metronomeSettings,
      onMetronomeClickTrigger
    );

    let countOffTime = Tone.Time(
      `${metronomeSettings.countOffMeasures}m`
    ).toSeconds();

    if (timeSignature.complexity === TimeSignatureComplexity.ALLA_BREVE) {
      countOffTime = countOffTime / 2;
    }

    elapsedTime += countOffTime;
  }

  const bodyStart = elapsedTime;
  let bodyEnd = bodyStart;

  for (let pass = 0; pass < passes; pass++) {
    if (metronomeSynth) {
      scheduleMetronomeMeasures(
        measures.length,
        metronomeSynth,
        elapsedTime,
        timeSignature,
        metronomeSettings,
        onMetronomeClickTrigger
      );
    }

    elapsedTime = scheduleMeasures(
      elapsedTime,
      playbackSynth,
      measures,
      pitch,
      timeSignature,
      onNoteTrigger
    );

    if (pass === 0) {
      bodyEnd = elapsedTime;
    }
  }

  if (isInfinite) {
    // Loop the single scheduled body; count-off (before bodyStart) plays once.
    Transport.loopStart = bodyStart;
    Transport.loopEnd = bodyEnd;
    Transport.loop = true;
  } else {
    Transport.schedule(() => onNoteTrigger(null), elapsedTime);
    Transport.schedule(stopPlayback, elapsedTime + TRAILING_TIME);
  }

  Transport.start();
};

export type MeasuresProvider = () => Measure[];
export type AdvanceHandler = (current: Measure[], next: Measure[]) => void;

// How far before a segment ends we schedule the following one, so its notes
// are lined up on the same transport timeline with no audible gap.
const AUTO_LOOKAHEAD = 0.25; // seconds

// Schedules `repeat` passes of the measures (plus per-measure metronome clicks
// when active) starting at `startTime`, and returns the time the segment ends.
const scheduleSegment = (
  startTime: number,
  synth: TriggerableSynth,
  metronomeSynth: TriggerableSynth | null,
  measures: Measure[],
  pitch: Pitch,
  timeSignature: TimeSignature,
  metronomeSettings: MetronomeSettings,
  onNoteTrigger: NoteTriggerHandler,
  onMetronomeClickTrigger: NoteTriggerHandler,
  repeat: number
): number => {
  const passes = isFinite(repeat) ? Math.max(1, Math.floor(repeat)) : 1;
  let time = startTime;

  for (let pass = 0; pass < passes; pass++) {
    if (metronomeSynth) {
      scheduleMetronomeMeasures(
        measures.length,
        metronomeSynth,
        time,
        timeSignature,
        metronomeSettings,
        onMetronomeClickTrigger
      );
    }
    time = scheduleMeasures(
      time,
      synth,
      measures,
      pitch,
      timeSignature,
      onNoteTrigger
    );
  }

  return time;
};

// Continuous auto playback: the transport never stops, so the pulse carries
// across rhythm changes. Each time the audio reaches the next segment, the UI
// is advanced (via Tone.Draw, synced to audio time) and a fresh "next" rhythm
// is generated and scheduled to follow — endless, gapless practice.
export const startAutoPlayback = (
  currentMeasures: Measure[],
  nextMeasures: Measure[],
  pitch: Pitch,
  timeSignature: TimeSignature,
  metronomeSettings: MetronomeSettings,
  onNoteTrigger: NoteTriggerHandler,
  onMetronomeClickTrigger: NoteTriggerHandler,
  repeat: number,
  requestNext: MeasuresProvider,
  onAdvance: AdvanceHandler
) => {
  startAudio();
  Transport.cancel();
  Tone.Draw.cancel(0);
  Transport.loop = false;
  Transport.timeSignature = timeSignature.beatsPerMeasure;

  const synth = getPlaybackSynth();
  const metronomeSynth = metronomeSettings.active ? getMetronomeSynth() : null;

  // Schedules the count-off clicks at `startTime` and returns the time the
  // body should start; a no-op (returns startTime unchanged) when the
  // metronome is off or no count-off measures are configured.
  const scheduleCountOff = (startTime: number): number => {
    if (!metronomeSynth || !metronomeSettings.countOffMeasures) {
      return startTime;
    }

    scheduleMetronomeMeasures(
      metronomeSettings.countOffMeasures,
      metronomeSynth,
      startTime,
      timeSignature,
      metronomeSettings,
      onMetronomeClickTrigger
    );

    let countOffTime = Tone.Time(
      `${metronomeSettings.countOffMeasures}m`
    ).toSeconds();

    if (timeSignature.complexity === TimeSignatureComplexity.ALLA_BREVE) {
      countOffTime = countOffTime / 2;
    }

    return startTime + countOffTime;
  };

  let elapsedTime = HEADING_TIME;

  // Two count-off modes: by default it plays once before the whole stream;
  // when countOffEveryRhythm is set, each rhythm gets its own count-off
  // (scheduled per segment below) instead.
  if (!metronomeSettings.countOffEveryRhythm) {
    elapsedTime = scheduleCountOff(elapsedTime);
  }

  // Schedules the body for one rhythm starting at `segmentStart` and returns
  // the time it ends. A per-rhythm count-off (when enabled) plays first, over
  // the already-advanced score.
  const scheduleBody = (
    segmentStart: number,
    segmentMeasures: Measure[]
  ): number => {
    const bodyStart = metronomeSettings.countOffEveryRhythm
      ? scheduleCountOff(segmentStart)
      : segmentStart;

    return scheduleSegment(
      bodyStart,
      synth,
      metronomeSynth,
      segmentMeasures,
      pitch,
      timeSignature,
      metronomeSettings,
      onNoteTrigger,
      onMetronomeClickTrigger,
      repeat
    );
  };

  const scheduleUpcoming = (segmentStart: number, segmentMeasures: Measure[]) => {
    const following = requestNext();

    // When the audio reaches this segment, promote it to the current score and
    // reveal the freshly generated "next" as the new grey preview. Tone.Draw
    // wants the AudioContext time, so hand it the `time` the Transport callback
    // receives — passing the transport-relative `segmentStart` directly would
    // place the event in the past and Draw would silently drop it.
    Transport.schedule((time) => {
      Tone.Draw.schedule(() => onAdvance(segmentMeasures, following), time);
    }, segmentStart);

    const segmentEnd = scheduleBody(segmentStart, segmentMeasures);

    Transport.schedule(() => {
      scheduleUpcoming(segmentEnd, following);
    }, Math.max(segmentStart, segmentEnd - AUTO_LOOKAHEAD));
  };

  // The first segment is already displayed, so it plays without an advance.
  const firstEnd = scheduleBody(elapsedTime, currentMeasures);

  Transport.schedule(() => {
    scheduleUpcoming(firstEnd, nextMeasures);
  }, Math.max(elapsedTime, firstEnd - AUTO_LOOKAHEAD));

  Transport.start();
};

export const stopPlayback = () => {
  Transport.stop();
  Transport.cancel();
  Tone.Draw.cancel(0);
  Transport.loop = false;
};

export const updateTempo = (tempo: number) => {
  Transport.bpm.value = tempo;
};

const getPitchString = (pitch: Pitch) => {
  return `${pitch.pitchClass}${pitch.octave}`;
};

// How much time a playback pattern takes up, in seconds.
const getPatternSeconds = (playbackPattern: PlaybackPattern): number => {
  // Tone.js treats `1n` (the whole note) as a whole measure, regardless of time signature.
  // Convert it to be strictly 4 beats. However, whole rests should take up the whole
  // measure, so no need to apply this behavior if the note is a rest
  if (playbackPattern.toneDuration === '1n' && !playbackPattern.rest) {
    return Tone.Time('4n').toSeconds() * 4;
  }

  // Handle dotted whole notes similarly
  if (playbackPattern.toneDuration === '1n.') {
    return Tone.Time('4n').toSeconds() * 6;
  }

  return Tone.Time(playbackPattern.toneDuration).toSeconds();
};

const triggerNote = (
  synth: TriggerableSynth,
  playbackPattern: PlaybackPattern,
  index: number,
  pitch: Pitch,
  onNoteTrigger: NoteTriggerHandler,
  // Time the notes tied to this one add to it, so a note split across the
  // middle of the measure sounds as one.
  tiedSeconds: number = 0
) => {
  return (time: number) => {
    onNoteTrigger(index);

    // A tie continuation keeps its slot, so note head indices stay aligned,
    // but it is never struck again: the note it hangs off already sounds
    // through it.
    if (playbackPattern.rest || playbackPattern.tieStop) {
      return;
    }

    const timeToAdd = getPatternSeconds(playbackPattern) + tiedSeconds;

    const toneDurationFactor = playbackPattern.toneDuration.replace(/\D/g, '');

    const noteSpacing = toneDurationFactorNoteSpacingMap[toneDurationFactor];

    synth.triggerAttackRelease(
      getPitchString(pitch),
      timeToAdd * noteSpacing,
      time,
      playbackPattern.velocity
    );
  };
};

// Schedules a single pass of the measures starting at `elapsedTime` and
// returns the time at which the pass ends. Note-highlight indices restart at 0
// each pass so every repeat highlights the same rendered note heads. Scheduling
// of the end-of-playback reset/stop is handled by the caller.
const scheduleMeasures = (
  elapsedTime: number,
  synth: TriggerableSynth,
  measures: Measure[],
  pitch: Pitch,
  timeSignature: TimeSignature,
  onNoteTrigger: NoteTriggerHandler
): number => {
  if (measures.length === 0) {
    return elapsedTime;
  }

  let playbackPatternIndex = 0;

  measures.forEach((measure) => {
    if (getTotalDuration(measure.noteGroups) === 0) {
      return;
    }

    measure.noteGroups.forEach((noteGroup) => {
      const playbackPatterns = getPlaybackPatternsForNoteGroup(
        noteGroup,
        timeSignature
      );

      if (playbackPatterns.length === 0) {
        return;
      }

      playbackPatterns.forEach((playbackPattern, patternIndex) => {
        // Time contributed by the notes tied to this one, so a note split at
        // the middle of the measure is heard as a single sustained note.
        let tiedSeconds = 0;

        for (let i = patternIndex + 1; i < playbackPatterns.length; i++) {
          if (!playbackPatterns[i].tieStop) {
            break;
          }

          tiedSeconds += getPatternSeconds(playbackPatterns[i]);
        }

        Transport.schedule(
          triggerNote(
            synth,
            playbackPattern,
            playbackPatternIndex,
            pitch,
            onNoteTrigger,
            tiedSeconds
          ),
          elapsedTime
        );

        elapsedTime += getPatternSeconds(playbackPattern);
        playbackPatternIndex += 1;
      });
    });
  });

  return elapsedTime;
};

const scheduleMetronome = (
  synth: TriggerableSynth,
  elapsedTime: number,
  timeSignature: TimeSignature,
  metronomeSettings: MetronomeSettings,
  scheduleNext: boolean,
  onMetronomeClickTrigger: NoteTriggerHandler
) => {
  const playbackPatterns = getMetronomePlaybackPatterns(
    timeSignature,
    metronomeSettings
  );

  playbackPatterns.forEach((playbackPattern, index) => {
    if (playbackPattern.pitch) {
      Transport.schedule(
        triggerNote(
          synth,
          playbackPattern,
          index,
          playbackPattern.pitch,
          (i) => {
            // Schedule next measure of clicks if scheduling last click
            if (scheduleNext && i === playbackPatterns.length - 1) {
              scheduleMetronome(
                synth,
                elapsedTime,
                timeSignature,
                metronomeSettings,
                true,
                onMetronomeClickTrigger
              );
            }

            onMetronomeClickTrigger(i);
          }
        ),
        elapsedTime
      );
    }

    elapsedTime += Tone.Time(playbackPattern.toneDuration).toSeconds();
  });

  return elapsedTime;
};

const scheduleMetronomeMeasures = (
  measureCount: number,
  synth: TriggerableSynth,
  elapsedTime: number,
  timeSignature: TimeSignature,
  metronomeSettings: MetronomeSettings,
  onMetronomeClickTrigger: NoteTriggerHandler
) => {
  for (let count = 0; count < measureCount; count++) {
    elapsedTime = scheduleMetronome(
      synth,
      elapsedTime,
      timeSignature,
      metronomeSettings,
      false,
      onMetronomeClickTrigger
    );
  }
};

export const startMetronome = (
  timeSignature: TimeSignature,
  metronomeSettings: MetronomeSettings,
  onMetronomeClickTrigger: NoteTriggerHandler
) => {
  startAudio();
  Transport.cancel();

  scheduleMetronome(
    getMetronomeSynth(),
    HEADING_TIME,
    timeSignature,
    metronomeSettings,
    true,
    onMetronomeClickTrigger
  );

  Transport.start();
};

export const stopMetronome = () => {
  Transport.stop();
  Transport.cancel();
};
